import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from './api';
import Spinner from './Spinner';
import CustomAlert from './CustomAlert';
import ExcelJS from 'exceljs';
import {
    buildAircraftNeeds,
    buildFleetIndex,
    buildCannibalizationMatrix,
    buildCannibalizationOpportunities,
    buildCrdLifeIndex,
    computeCannibalizationStats,
    getCannibalizationParts,
    todayLocal,
} from './cannibalizationUtils';
import { useSiteNames } from './useSiteCatalog';

const ALL_SITES = 'ALL';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        bg: isDark ? '#121212' : '#f4f7f6',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
        headerBg: isDark ? '#2a2a2a' : '#e9eef3',
        rowAlt: isDark ? '#262626' : '#fafbfc',
        donorBg: isDark ? '#1a3324' : '#e8f5e9',
        needBg: isDark ? '#3d1f24' : '#fdecea',
        highlightCol: isDark ? '#2a3f5f' : '#e3f2fd',
    };

    return {
        page: {
            backgroundColor: colors.bg,
            minHeight: '100vh',
            padding: '20px',
            color: colors.text,
        },
        headerCard: {
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: `0 2px 5px ${colors.shadow}`,
            border: `1px solid ${colors.border}`,
        },
        navLink: {
            color: colors.link,
            textDecoration: 'none',
            fontSize: '14px',
            display: 'inline-block',
            marginBottom: '10px',
        },
        title: {
            fontSize: '24px',
            fontWeight: 'bold',
            color: colors.text,
            margin: '0 0 6px 0',
        },
        subtitle: {
            fontSize: '14px',
            color: colors.subtleText,
            margin: 0,
        },
        assumptionBanner: {
            backgroundColor: isDark ? '#2a2a2a' : '#fff8e1',
            border: `1px solid ${isDark ? '#555' : '#ffe082'}`,
            borderRadius: '6px',
            padding: '10px 14px',
            marginBottom: '16px',
            fontSize: '12px',
            color: colors.text,
            lineHeight: 1.5,
        },
        section: {
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            boxShadow: `0 2px 5px ${colors.shadow}`,
            border: `1px solid ${colors.border}`,
            overflow: 'hidden',
            marginBottom: '20px',
        },
        sectionHeader: {
            padding: '15px 20px',
            borderBottom: `2px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: colors.headerBg,
            flexWrap: 'wrap',
            gap: '10px',
        },
        sectionTitle: {
            fontSize: '18px',
            fontWeight: 'bold',
            color: colors.text,
            margin: 0,
        },
        controls: {
            display: 'flex',
            gap: '12px',
            padding: '12px 20px',
            borderBottom: `1px solid ${colors.border}`,
            flexWrap: 'wrap',
            alignItems: 'center',
        },
        input: {
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
            minWidth: '200px',
        },
        select: {
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
        },
        exportButton: {
            padding: '8px 16px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px',
        },
        statsRow: {
            display: 'flex',
            gap: '12px',
            padding: '12px 20px',
            flexWrap: 'wrap',
            borderBottom: `1px solid ${colors.border}`,
        },
        statChip: {
            backgroundColor: isDark ? '#2a2a2a' : '#f0f4f8',
            borderRadius: '6px',
            padding: '10px 14px',
            minWidth: '120px',
            border: `1px solid ${colors.border}`,
        },
        statLabel: {
            fontSize: '10px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: colors.subtleText,
            marginBottom: '4px',
        },
        statValue: {
            fontSize: '22px',
            fontWeight: 'bold',
            color: colors.text,
        },
        tableWrap: {
            overflowX: 'auto',
            overflowY: 'auto',
            maxHeight: '520px',
        },
        matrixWrap: {
            overflowX: 'auto',
            overflowY: 'auto',
            maxHeight: '480px',
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
        },
        th: {
            position: 'sticky',
            top: 0,
            padding: '10px 8px',
            textAlign: 'left',
            fontSize: '11px',
            fontWeight: 'bold',
            color: colors.text,
            backgroundColor: colors.headerBg,
            borderBottom: `2px solid ${colors.border}`,
            cursor: 'pointer',
            userSelect: 'none',
            whiteSpace: 'nowrap',
            zIndex: 2,
        },
        thStickyLeft: {
            left: 0,
            zIndex: 3,
        },
        thPart: {
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)',
            maxWidth: '36px',
            minWidth: '36px',
            height: '120px',
            textAlign: 'left',
            cursor: 'pointer',
        },
        td: {
            padding: '6px 8px',
            fontSize: '11px',
            color: colors.text,
            borderBottom: `1px solid ${colors.border}`,
            whiteSpace: 'nowrap',
        },
        tdStickyLeft: {
            position: 'sticky',
            left: 0,
            backgroundColor: colors.cardBg,
            zIndex: 1,
            fontWeight: 'bold',
        },
        tdNeed: {
            backgroundColor: colors.needBg,
            textAlign: 'center',
            fontWeight: 'bold',
            color: isDark ? '#ffb4ab' : '#b71c1c',
        },
        tdDonor: {
            backgroundColor: colors.donorBg,
            textAlign: 'center',
            color: isDark ? '#8fd694' : '#2e7d32',
        },
        tdHighlightCol: {
            outline: `2px solid ${isDark ? '#6cb2f7' : '#1976d2'}`,
        },
        rowAlt: {
            backgroundColor: colors.rowAlt,
        },
        loadingBox: {
            display: 'flex',
            justifyContent: 'center',
            padding: '40px',
        },
        empty: {
            padding: '40px 20px',
            textAlign: 'center',
            color: colors.subtleText,
        },
        badge: {
            display: 'inline-block',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 'bold',
        },
        badgeYes: {
            backgroundColor: '#28a745',
            color: '#fff',
        },
        badgeNo: {
            backgroundColor: isDark ? '#444' : '#dee2e6',
            color: colors.text,
        },
        legend: {
            display: 'flex',
            gap: '16px',
            padding: '8px 20px 12px',
            fontSize: '11px',
            flexWrap: 'wrap',
        },
        legendItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
        },
        legendSwatch: {
            width: '14px',
            height: '14px',
            borderRadius: '3px',
            border: `1px solid ${colors.border}`,
        },
        checkboxLabel: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '13px',
            cursor: 'pointer',
        },
    };
};

function CannibalizationPage({ theme, embedded = false }) {
    const styles = getStyles(theme);
    const siteNames = useSiteNames();
    const siteOptions = [ALL_SITES, ...siteNames];
    const [siteFilter, setSiteFilter] = useState(ALL_SITES);
    const [rawData, setRawData] = useState([]);
    const [fleetAircraft, setFleetAircraft] = useState([]);
    const [lastFlyByBuno, setLastFlyByBuno] = useState({});
    const [crdRows, setCrdRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [repairableOnly, setRepairableOnly] = useState(true);
    const [singleHitOnly, setSingleHitOnly] = useState(false);
    const [selectedPartNorm, setSelectedPartNorm] = useState('');
    const [sortField, setSortField] = useState('score');
    const [sortDirection, setSortDirection] = useState('desc');
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    const [exporting, setExporting] = useState(false);

    const fetchData = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (siteFilter && siteFilter !== ALL_SITES) {
            params.append('site_name', siteFilter);
        }
        const qs = params.toString();

        Promise.all([
            api.get(`/nmcs-pmcs/${qs ? `?${qs}` : ''}`),
            api.get('/aircraft/'),
            api.get('/aircraft-status/'),
            api.get(`/crd-all/${qs ? `?${qs}` : ''}`).catch(err => {
                console.warn('CRD All data unavailable for cannibalization ranking:', err);
                return { data: [] };
            }),
        ])
            .then(([nmcsRes, aircraftRes, statusRes, crdRes]) => {
                const nmcs = Array.isArray(nmcsRes.data) ? nmcsRes.data : (nmcsRes.data.results || []);
                const aircraft = Array.isArray(aircraftRes.data) ? aircraftRes.data : (aircraftRes.data.results || []);
                const statuses = Array.isArray(statusRes.data) ? statusRes.data : (statusRes.data.results || []);
                const crd = Array.isArray(crdRes.data) ? crdRes.data : (crdRes.data.results || []);

                const lfdMap = {};
                for (const s of statuses) {
                    const buno = (s.buno || '').toString().trim();
                    if (buno && s.last_fly_date) lfdMap[buno] = s.last_fly_date;
                }

                setRawData(nmcs);
                setFleetAircraft(aircraft);
                setLastFlyByBuno(lfdMap);
                setCrdRows(crd);
            })
            .catch(err => {
                console.error('Error fetching cannibalization data:', err);
                setAlert({ show: true, message: 'Failed to fetch cannibalization data.', type: 'error' });
                setRawData([]);
                setFleetAircraft([]);
                setCrdRows([]);
            })
            .finally(() => setLoading(false));
    }, [siteFilter]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        setSelectedPartNorm('');
    }, [siteFilter]);

    const ak0Rows = useMemo(() => (
        (rawData || []).filter(r =>
            (r.proj || '').toString().trim().toUpperCase() === 'AK0'
        )
    ), [rawData]);

    const needsByAircraft = useMemo(
        () => buildAircraftNeeds(ak0Rows, siteFilter),
        [ak0Rows, siteFilter]
    );

    const { bySite: fleetBySite } = useMemo(
        () => buildFleetIndex(fleetAircraft, ALL_SITES),
        [fleetAircraft]
    );

    const crdLifeByKey = useMemo(
        () => buildCrdLifeIndex(crdRows, ALL_SITES),
        [crdRows]
    );

    const parts = useMemo(
        () => getCannibalizationParts(needsByAircraft, { repairableOnly }),
        [needsByAircraft, repairableOnly]
    );

    const opportunities = useMemo(
        () => buildCannibalizationOpportunities({
            needsByAircraft,
            fleetBySite,
            lastFlyByBuno,
            crdLifeByKey,
            siteFilter,
            repairableOnly,
            singleHitOnly,
        }),
        [needsByAircraft, fleetBySite, lastFlyByBuno, crdLifeByKey, siteFilter, repairableOnly, singleHitOnly]
    );

    const stats = useMemo(
        () => computeCannibalizationStats(opportunities, parts, needsByAircraft),
        [opportunities, parts, needsByAircraft]
    );

    const matrixSite = siteFilter !== ALL_SITES ? siteFilter : null;

    const matrix = useMemo(() => {
        if (!matrixSite) return null;
        return buildCannibalizationMatrix({
            needsByAircraft,
            fleetBySite,
            parts,
            site: matrixSite,
            lastFlyByBuno,
            crdLifeByKey,
        });
    }, [matrixSite, needsByAircraft, fleetBySite, parts, lastFlyByBuno, crdLifeByKey]);

    const filteredOpportunities = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return opportunities;
        return opportunities.filter(o => {
            const hay = `${o.partNo} ${o.nomen} ${o.receiverBuno} ${o.donorBuno} ${o.site}`.toLowerCase();
            return hay.includes(term);
        });
    }, [opportunities, searchTerm]);

    const sortedOpportunities = useMemo(() => {
        const copy = [...filteredOpportunities];
        const dir = sortDirection === 'asc' ? 1 : -1;
        copy.sort((a, b) => {
            let av = a[sortField];
            let bv = b[sortField];
            if (typeof av === 'string') av = av.toLowerCase();
            if (typeof bv === 'string') bv = bv.toLowerCase();
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            if (av < bv) return -1 * dir;
            if (av > bv) return 1 * dir;
            return 0;
        });
        return copy;
    }, [filteredOpportunities, sortField, sortDirection]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection(field === 'score' ? 'desc' : 'asc');
        }
    };

    const sortIcon = (field) => {
        if (sortField !== field) return '';
        return sortDirection === 'asc' ? ' ▲' : ' ▼';
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const workbook = new ExcelJS.Workbook();
            const titleSuffix = siteFilter === ALL_SITES ? 'All Sites' : siteFilter;
            const today = todayLocal();
            const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

            if (matrix && matrix.rows.length > 0) {
                const matrixSheet = workbook.addWorksheet('Matrix');
                matrixSheet.addRow([`Cannibalization Matrix - ${titleSuffix}`]);
                matrixSheet.getCell('A1').font = { size: 14, bold: true };
                matrixSheet.addRow(['Filled = AK0 need | Green = multi-hit donor with part installed']);
                matrixSheet.addRow([]);

                const header = ['BUNO', 'AK0 Count', 'Single Hit', ...matrix.parts.map(p => p.partNo)];
                matrixSheet.addRow(header).font = { bold: true };

                matrix.rows.forEach((row, ri) => {
                    const cells = matrix.cells[ri];
                    matrixSheet.addRow([
                        row.buno,
                        row.ak0Count,
                        row.isSingleHit ? 'Yes' : 'No',
                        ...cells.map(c => (c.state === 'need' ? (c.eddStr || 'Need') : '')),
                    ]);
                });
            }

            const oppSheet = workbook.addWorksheet('Opportunities');
            oppSheet.addRow([`Cannibalization Opportunities - ${titleSuffix}`]);
            oppSheet.getCell('A1').font = { size: 14, bold: true };
            oppSheet.addRow(['Donor rule: multi-hit tail only, target part not on donor AK0; CRD life remaining preferred']);
            oppSheet.addRow([]);
            oppSheet.addRow([
                'Site', 'Part Number', 'Nomenclature', 'Receiver BUNO', 'Receiver Single Hit',
                'Receiver AK0 Count', 'Receiver EDD', 'EDD Days Out', 'Donor BUNO',
                'Donor AK0 Count', 'Donor Hrs Remng', 'Donor Schd Dt', 'EDD Days Saved', 'Score',
            ]).font = { bold: true };

            sortedOpportunities.forEach(o => {
                oppSheet.addRow([
                    o.site,
                    o.partNo,
                    o.nomen,
                    o.receiverBuno,
                    o.receiverSingleHit ? 'Yes' : 'No',
                    o.receiverAk0Count,
                    o.receiverEddStr,
                    o.receiverEddDaysOut ?? '',
                    o.donorBuno,
                    o.donorAk0Count,
                    o.donorUsgRemngQty ?? '',
                    o.donorSchdDtStr || '',
                    o.eddDaysSaved ?? '',
                    o.score,
                ]);
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Cannibalization_${siteFilter === ALL_SITES ? 'AllSites' : siteFilter}_${dateStr}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            setAlert({ show: true, message: 'Excel file exported successfully.', type: 'success' });
        } catch (err) {
            console.error('Cannibalization export error:', err);
            setAlert({ show: true, message: 'Failed to export Excel file.', type: 'error' });
        } finally {
            setExporting(false);
        }
    };

    const renderMatrixCell = (cell, partNorm, colIdx) => {
        const highlight = selectedPartNorm && selectedPartNorm === partNorm;
        const baseStyle = {
            ...styles.td,
            ...(highlight ? styles.tdHighlightCol : {}),
        };

        if (cell.state === 'need') {
            const label = cell.eddStr || 'Need';
            const title = cell.eddDaysOut != null
                ? `Need — EDD ${label} (${cell.eddDaysOut} days)`
                : `Need — no EDD`;
            return (
                <td
                    key={colIdx}
                    style={{ ...baseStyle, ...styles.tdNeed }}
                    title={title}
                >
                    {cell.eddDaysOut != null && cell.eddDaysOut < 0 ? '!' : '●'}
                    <div style={{ fontSize: '9px', fontWeight: 'normal' }}>{label}</div>
                </td>
            );
        }

        if (cell.state === 'donor') {
            const lifeBits = [];
            if (cell.usgRemngQty != null) lifeBits.push(`${Number(cell.usgRemngQty).toFixed(1)} hrs remng`);
            if (cell.schdDtStr) lifeBits.push(`due ${cell.schdDtStr}`);
            const title = lifeBits.length
                ? `CRD-verified donor — ${lifeBits.join(', ')}`
                : 'CRD-verified donor (multi-hit, part present in CRD)';
            return (
                <td
                    key={colIdx}
                    style={{ ...baseStyle, ...styles.tdDonor }}
                    title={title}
                />
            );
        }

        return (
            <td
                key={colIdx}
                style={baseStyle}
                title="Not a donor (UP aircraft, single-hit, or waiting on this part)"
            />
        );
    };

    return (
        <div style={embedded ? undefined : styles.page}>
            <CustomAlert {...alert} onClose={() => setAlert({ show: false })} theme={theme} />

            {!embedded && (
                <div style={styles.headerCard}>
                    <Link to="/" style={styles.navLink}>&larr; Back to Home</Link>
                    <h1 style={styles.title}>Cannibalization</h1>
                    <p style={styles.subtitle}>
                        Cross-tail repairable part opportunities from AK0 supply data.
                        Donors are multi-hit tails with the needed part still installed — not UP aircraft.
                    </p>
                </div>
            )}

            <div style={styles.assumptionBanner}>
                <strong>Donor algorithm:</strong> UP / supply-clear tails (0 AK0) are never donors.
                Only tails with multiple open AK0 lines may donate, and only for parts they still have
                installed (no open AK0 on that part). When CRD All is uploaded, a donor must also have
                that part number present in CRD — AK0 absence alone is not enough. Ranking prefers
                healthier remaining life (higher <code>usg_remng_qty</code> and/or later <code>schd_dt</code>).
                Parts that never appear in CRD will not get donor suggestions while CRD data is loaded.
                Repairable parts only by default. Matrix view requires a single site selection.
            </div>

            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <h2 style={styles.sectionTitle}>Cannibalization Analysis</h2>
                    <button
                        onClick={handleExport}
                        disabled={exporting || loading || sortedOpportunities.length === 0}
                        style={{
                            ...styles.exportButton,
                            opacity: (exporting || loading || sortedOpportunities.length === 0) ? 0.6 : 1,
                            cursor: (exporting || loading || sortedOpportunities.length === 0) ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {exporting ? 'Exporting...' : 'Export to Excel'}
                    </button>
                </div>

                <div style={styles.controls}>
                    <select
                        value={siteFilter}
                        onChange={(e) => setSiteFilter(e.target.value)}
                        style={styles.select}
                    >
                        {siteOptions.map(s => (
                            <option key={s} value={s}>{s === ALL_SITES ? 'All Sites' : s}</option>
                        ))}
                    </select>
                    <input
                        type="text"
                        placeholder="Search part, BUNO, or nomenclature..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={styles.input}
                    />
                    <label style={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            checked={repairableOnly}
                            onChange={(e) => setRepairableOnly(e.target.checked)}
                        />
                        Repairable only
                    </label>
                    <label style={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            checked={singleHitOnly}
                            onChange={(e) => setSingleHitOnly(e.target.checked)}
                        />
                        Single-hit receivers only
                    </label>
                </div>

                <div style={styles.statsRow}>
                    <div style={styles.statChip}>
                        <div style={styles.statLabel}>Opportunities</div>
                        <div style={styles.statValue}>{stats.opportunityCount}</div>
                    </div>
                    <div style={styles.statChip}>
                        <div style={styles.statLabel}>Repairable Parts</div>
                        <div style={styles.statValue}>{stats.repairableParts}</div>
                    </div>
                    <div style={styles.statChip}>
                        <div style={styles.statLabel}>Receivers Matched</div>
                        <div style={styles.statValue}>{stats.receiversMatched}</div>
                    </div>
                    <div style={styles.statChip}>
                        <div style={styles.statLabel}>Single-Hit Ops</div>
                        <div style={styles.statValue}>{stats.singleHitOpportunities}</div>
                    </div>
                    <div style={styles.statChip}>
                        <div style={styles.statLabel}>Aircraft w/ AK0</div>
                        <div style={styles.statValue}>{stats.aircraftWithAk0}</div>
                    </div>
                </div>

                {loading ? (
                    <div style={styles.loadingBox}><Spinner /></div>
                ) : (
                    <>
                        <div style={styles.sectionHeader}>
                            <h3 style={{ ...styles.sectionTitle, fontSize: '16px' }}>BUNO × Part Matrix</h3>
                        </div>
                        {matrixSite ? (
                            matrix && matrix.rows.length > 0 && matrix.parts.length > 0 ? (
                                <>
                                    <div style={styles.legend}>
                                        <div style={styles.legendItem}>
                                            <span style={{ ...styles.legendSwatch, backgroundColor: theme === 'dark' ? '#3d1f24' : '#fdecea' }} />
                                            Need (open AK0)
                                        </div>
                                        <div style={styles.legendItem}>
                                            <span style={{ ...styles.legendSwatch, backgroundColor: theme === 'dark' ? '#1a3324' : '#e8f5e9' }} />
                                            Potential donor (multi-hit + CRD-verified part)
                                        </div>
                                        <span style={{ color: theme === 'dark' ? '#a0a0a0' : '#666' }}>
                                            Click a part column to highlight donors vs receivers
                                        </span>
                                    </div>
                                    <div style={styles.matrixWrap}>
                                        <table style={styles.table}>
                                            <thead>
                                                <tr>
                                                    <th style={{ ...styles.th, ...styles.thStickyLeft, minWidth: '70px' }}>BUNO</th>
                                                    <th style={{ ...styles.th, minWidth: '50px' }}>AK0</th>
                                                    {matrix.parts.map(p => (
                                                        <th
                                                            key={p.norm}
                                                            style={{
                                                                ...styles.th,
                                                                ...styles.thPart,
                                                                ...(selectedPartNorm === p.norm ? styles.tdHighlightCol : {}),
                                                            }}
                                                            title={`${p.partNo} — ${p.nomen || ''} (${p.receiverCount} receivers)`}
                                                            onClick={() => setSelectedPartNorm(
                                                                selectedPartNorm === p.norm ? '' : p.norm
                                                            )}
                                                        >
                                                            {p.partNo}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {matrix.rows.map((row, ri) => (
                                                    <tr key={row.key} style={ri % 2 ? styles.rowAlt : undefined}>
                                                        <td style={{ ...styles.td, ...styles.tdStickyLeft }}>
                                                            {row.buno}
                                                            {row.isSingleHit && (
                                                                <span style={{ ...styles.badge, ...styles.badgeYes, marginLeft: '4px' }}>1</span>
                                                            )}
                                                        </td>
                                                        <td style={{ ...styles.td, textAlign: 'center' }}>{row.ak0Count || '—'}</td>
                                                        {matrix.cells[ri].map((cell, ci) =>
                                                            renderMatrixCell(cell, matrix.parts[ci].norm, ci)
                                                        )}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            ) : (
                                <div style={styles.empty}>
                                    No repairable AK0 parts with open needs at {matrixSite}.
                                </div>
                            )
                        ) : (
                            <div style={styles.empty}>
                                Select a single site to view the BUNO × Part matrix.
                            </div>
                        )}

                        <div style={styles.sectionHeader}>
                            <h3 style={{ ...styles.sectionTitle, fontSize: '16px' }}>
                                Ranked Opportunities ({sortedOpportunities.length})
                            </h3>
                        </div>

                        {sortedOpportunities.length === 0 ? (
                            <div style={styles.empty}>
                                No cannibalization opportunities found for the current filters.
                            </div>
                        ) : (
                            <div style={styles.tableWrap}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            {siteFilter === ALL_SITES && (
                                                <th style={styles.th} onClick={() => handleSort('site')}>
                                                    Site{sortIcon('site')}
                                                </th>
                                            )}
                                            <th style={styles.th} onClick={() => handleSort('partNo')}>
                                                Part{sortIcon('partNo')}
                                            </th>
                                            <th style={styles.th} onClick={() => handleSort('receiverBuno')}>
                                                Receiver{sortIcon('receiverBuno')}
                                            </th>
                                            <th style={styles.th}>Single Hit</th>
                                            <th style={styles.th} onClick={() => handleSort('receiverEddDaysOut')}>
                                                EDD Days{sortIcon('receiverEddDaysOut')}
                                            </th>
                                            <th style={styles.th} onClick={() => handleSort('donorBuno')}>
                                                Donor{sortIcon('donorBuno')}
                                            </th>
                                            <th style={styles.th} onClick={() => handleSort('donorAk0Count')}>
                                                Donor AK0{sortIcon('donorAk0Count')}
                                            </th>
                                            <th style={styles.th} onClick={() => handleSort('donorUsgRemngQty')}>
                                                Donor Life{sortIcon('donorUsgRemngQty')}
                                            </th>
                                            <th style={styles.th} onClick={() => handleSort('eddDaysSaved')}>
                                                Days Saved{sortIcon('eddDaysSaved')}
                                            </th>
                                            <th style={styles.th} onClick={() => handleSort('score')}>
                                                Score{sortIcon('score')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedOpportunities.map((o, i) => (
                                            <tr
                                                key={o.id}
                                                style={i % 2 ? styles.rowAlt : undefined}
                                                onClick={() => setSelectedPartNorm(o.partNorm)}
                                            >
                                                {siteFilter === ALL_SITES && (
                                                    <td style={styles.td}>{o.site}</td>
                                                )}
                                                <td style={styles.td} title={o.nomen}>
                                                    <div style={{ fontWeight: 'bold' }}>{o.partNo}</div>
                                                    <div style={{ fontSize: '10px', opacity: 0.8 }}>{o.nomen}</div>
                                                </td>
                                                <td style={styles.td}>
                                                    {o.receiverBuno}
                                                    <div style={{ fontSize: '10px' }}>{o.receiverAk0Count} AK0</div>
                                                </td>
                                                <td style={styles.td}>
                                                    <span style={{
                                                        ...styles.badge,
                                                        ...(o.receiverSingleHit ? styles.badgeYes : styles.badgeNo),
                                                    }}>
                                                        {o.receiverSingleHit ? 'Yes' : 'No'}
                                                    </span>
                                                </td>
                                                <td style={styles.td}>
                                                    {o.receiverEddStr || '—'}
                                                    {o.receiverEddDaysOut != null && (
                                                        <div style={{
                                                            fontSize: '10px',
                                                            color: o.receiverEddDaysOut < 0 ? '#dc3545' : undefined,
                                                        }}>
                                                            {o.receiverEddDaysOut}d
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={styles.td}>{o.donorBuno}</td>
                                                <td style={styles.td}>{o.donorAk0Count}</td>
                                                <td style={styles.td} title={o.donorLifeStr || 'No CRD life data'}>
                                                    {o.donorUsgRemngQty != null
                                                        ? `${Number(o.donorUsgRemngQty).toFixed(1)} hrs`
                                                        : (o.donorSchdDtStr || '—')}
                                                </td>
                                                <td style={styles.td}>
                                                    {o.eddDaysSaved != null ? o.eddDaysSaved : '—'}
                                                </td>
                                                <td style={{ ...styles.td, fontWeight: 'bold' }}>{o.score}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default CannibalizationPage;
